import { useEffect, useState, useContext, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import WhiteMainBackground from "../../../shared_components/WhiteMainBackground/WhiteMainBackground";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleChevronLeft } from "@fortawesome/free-solid-svg-icons";
import AnimationGeneratorForm from "../AnimationGeneratorForm/AnimationGeneratorForm";
import AnimationStatusPanel from "../AnimationStatusPanel/AnimationStatusPanel";
import useAnimationGeneration from "../hooks/useAnimationGeneration";
import useAnimationPolling from "../hooks/useAnimationPolling";
import AnimationService from "../../../services/AnimationService";
import UserData from "../../../services/UserData";
import Permissions from "../../../services/Permissions";
import AIAnimationService from "../../../services/AIAnimationService";
import NotFound from "../../NotFound/NotFound";
import { resolveVideoId, resolveVideoIdFromStatus, isSuccessStatus } from "../utils";
import styles from "./AiAnimationGeneratorView.module.css";

function AiAnimationGeneratorView() {
  const navigate = useNavigate();
  const userData = useContext(UserData);

  const {
    generateAnimation,
    animationGuid,
    isLoading,
    setIsLoading,
    isSubmitting,
  } = useAnimationGeneration();

  const [statusData, setStatusData] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [isPreparingPlayback, setIsPreparingPlayback] = useState(false);

  const onPreparingPlaybackChange = useCallback((value) => {
    setIsPreparingPlayback(value);
  }, []);

  useAnimationPolling(
    animationGuid,
    userData,
    setStatusData,
    setPreviewData,
    setVideoUrl,
    setIsLoading,
    setErrorMessage,
    onPreparingPlaybackChange
  );

  useEffect(() => {
    return () => {
      if (videoUrl && videoUrl.startsWith("blob:")) {
        URL.revokeObjectURL(videoUrl);
      }
    };
  }, [videoUrl]);

  const resolvedVideoId = useMemo(
    () => resolveVideoIdFromStatus(statusData, resolveVideoId(previewData)),
    [previewData, statusData]
  );

  // If the auto-stream in the polling hook failed silently, retry loading the
  // video blob once the job is complete and we have a video ID.
  useEffect(() => {
    if (!resolvedVideoId || videoUrl || isPreparingPlayback) return;
    if (!statusData || !isSuccessStatus(statusData)) return;

    let cancelled = false;
    const tryLoadVideo = async () => {
      const maxAttempts = 3;
      const delayMs = 2000;
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
          const streamResponse = await AnimationService.streamVideo(
            resolvedVideoId,
            userData.jwtToken,
            userData
          );
          const blob = await streamResponse.blob();
          if (!cancelled) {
            setVideoUrl(URL.createObjectURL(blob));
          }
          return;
        } catch (error) {
          if (attempt === maxAttempts) {
            if (!cancelled) {
              setErrorMessage("Animation generated successfully, but the preview is still loading. You can also view it in the Animation Gallery.");
            }
          } else {
            await new Promise((resolve) => setTimeout(resolve, delayMs));
          }
        }
      }
    };

    tryLoadVideo();
    return () => { cancelled = true; };
  }, [resolvedVideoId, videoUrl, isPreparingPlayback, statusData, userData, setErrorMessage]);

  const isJobProcessing = isLoading;
  const flowBusy =
    isSubmitting || isJobProcessing || isPreparingPlayback;

  const handleGenerate = async (formData) => {
    setErrorMessage(null);
    setStatusData({ status: "PENDING", percentage: 0 });
    setPreviewData(null);
    setVideoUrl(null);

    const result = await generateAnimation(formData);
    if (result?.status === "FAIL") {
      setErrorMessage(
        `Animation generation failed: ${result.error || "Unknown error"}`
      );
      setStatusData(null);
    }
  };

  if (!Permissions.isAnyOfTheRolesAllowed(userData, AIAnimationService.createRoles)) {
    return <NotFound />;
  }

  return (
    <WhiteMainBackground className={styles.whiteMainBackground}>
      <div className={styles.heading}>
        <FontAwesomeIcon
          className={styles.back_button}
          icon={faCircleChevronLeft}
          onClick={() => navigate("/animations")}
          tabIndex={0}
          aria-label="Go back"
        />
        <span>New Animation Asset</span>
      </div>
      <div className={styles.mainContainer}>
        <AnimationGeneratorForm
          onSubmit={handleGenerate}
          isFormDisabled={flowBusy}
          isSubmitting={isSubmitting}
        />
        <AnimationStatusPanel
          statusData={statusData}
          errorMessage={errorMessage}
          onDismissError={() => setErrorMessage(null)}
          videoUrl={videoUrl}
          isSubmitting={isSubmitting}
          isJobProcessing={isJobProcessing}
          isPreparingPlayback={isPreparingPlayback}
        />
      </div>
    </WhiteMainBackground>
  );
}

export default AiAnimationGeneratorView;
